/**
 * Device Status Utilities
 * OMNI IQ (c) 2025 Silver Falls Capital. All Rights Reserved
 *
 * ============================================================================
 * IMPORTANT: SINGLE SOURCE OF TRUTH FOR DEVICE STATUS
 * ============================================================================
 * This file must be kept synchronized between:
 * - Web App: /fe/src/utils/deviceStatus.ts
 * - Mobile App: /mobile-app/src/utils/deviceStatus.ts
 * - API: /api/src/lib/helpers/deviceStatus.ts
 *
 * Any changes to status logic MUST be made in all three places to maintain
 * consistency across all platforms.
 * ============================================================================
 */

/**
 * Device offline threshold in minutes
 * A device is considered offline if it hasn't reported data in this many minutes
 */
export const DEVICE_OFFLINE_THRESHOLD_MINUTES = 5

/**
 * Device offline threshold in milliseconds
 */
export const DEVICE_OFFLINE_THRESHOLD_MS = DEVICE_OFFLINE_THRESHOLD_MINUTES * 60 * 1000

/**
 * Check if a device is offline based on its last seen timestamp
 * @param lastSeenTimestamp - ISO timestamp string or Date when device was last seen
 * @returns true if device is offline (no data for > 5 minutes), false otherwise
 */
export function isDeviceOffline(lastSeenTimestamp: string | Date | null | undefined): boolean {
  if (!lastSeenTimestamp) {
    return true // No timestamp = offline
  }

  const lastSeenDate = typeof lastSeenTimestamp === 'string'
    ? new Date(lastSeenTimestamp)
    : lastSeenTimestamp

  const now = new Date()
  const diffMinutes = (now.getTime() - lastSeenDate.getTime()) / (1000 * 60)

  return diffMinutes > DEVICE_OFFLINE_THRESHOLD_MINUTES
}

/**
 * Format the time since last seen in a human-readable format
 * @param lastSeenTimestamp - ISO timestamp string or Date when device was last seen
 * @returns Formatted string like "5m ago", "2h ago", "3d ago"
 */
export function formatTimeSinceLastSeen(lastSeenTimestamp: string | Date | null | undefined): string {
  if (!lastSeenTimestamp) {
    return ''
  }

  const lastSeenDate = typeof lastSeenTimestamp === 'string'
    ? new Date(lastSeenTimestamp)
    : lastSeenTimestamp

  const now = new Date()
  const diffMinutes = (now.getTime() - lastSeenDate.getTime()) / (1000 * 60)

  if (diffMinutes < 1) {
    return 'Just now'
  } else if (diffMinutes < 60) {
    return `${Math.floor(diffMinutes)}m ago`
  } else if (diffMinutes < 1440) { // Less than 24 hours
    return `${Math.floor(diffMinutes / 60)}h ago`
  } else {
    return `${Math.floor(diffMinutes / 1440)}d ago`
  }
}

/**
 * Get minutes since last seen
 * @param lastSeenTimestamp - ISO timestamp string or Date when device was last seen
 * @returns Number of minutes since last seen, or null if no timestamp
 */
export function getMinutesSinceLastSeen(lastSeenTimestamp: string | Date | null | undefined): number | null {
  if (!lastSeenTimestamp) {
    return null
  }

  const lastSeenDate = typeof lastSeenTimestamp === 'string'
    ? new Date(lastSeenTimestamp)
    : lastSeenTimestamp

  const now = new Date()
  return (now.getTime() - lastSeenDate.getTime()) / (1000 * 60)
}

/**
 * Zone Alarm States (matching frontend enum)
 * IMPORTANT: These must match the alarm state values from the API
 */
export const enum ZoneAlarmState {
  NOT_ACTIVE = 0,
  NORMAL = 1,
  WARN = 2,
  ALARM = 3,
  ALARM_SILENCED = 4,
  ALARM_ACKNOWLEDGED = 5,
  NORMAL_PAST_UNACK_ALARM = 6,
  WARN_PAST_UNACK_ALARM = 7
}

/**
 * Device status priority (from highest to lowest):
 * 1. Alarm (Offline) - device in alarm state but offline
 * 2. Alarm (critical)
 * 3. Warning
 * 4. Offline (no data for > 5 minutes)
 * 5. Online (normal)
 */
export type DeviceStatus = 'Alarm (Offline)' | 'Alarm' | 'Warning' | 'Offline' | 'Online' | 'No Data'

/**
 * Check if alarm state is active (critical alarm)
 */
export function isAlarmActive(state: number | null | undefined): boolean {
  if (state === null || state === undefined) return false

  switch (state) {
    case ZoneAlarmState.ALARM:
    case ZoneAlarmState.ALARM_SILENCED:
    case ZoneAlarmState.ALARM_ACKNOWLEDGED:
      return true
    default:
      return false
  }
}

/**
 * Check if alarm state is warning
 */
export function isWarningState(state: number | null | undefined): boolean {
  if (state === null || state === undefined) return false

  switch (state) {
    case ZoneAlarmState.WARN:
    case ZoneAlarmState.WARN_PAST_UNACK_ALARM:
      return true
    default:
      return false
  }
}

/**
 * Get device status with correct priority
 * Priority: Alarm (Offline) > Alarm > Warning > Offline > Online
 *
 * @param dp0AlarmState - Zone 1 alarm state
 * @param dp1AlarmState - Zone 2 alarm state
 * @param lastSeenTimestamp - Last seen timestamp
 * @returns Device status
 */
export function getDeviceStatus(
  dp0AlarmState: number | null | undefined,
  dp1AlarmState: number | null | undefined,
  lastSeenTimestamp: string | Date | null | undefined
): DeviceStatus {
  const hasAlarm = isAlarmActive(dp0AlarmState) || isAlarmActive(dp1AlarmState)
  const isOffline = isDeviceOffline(lastSeenTimestamp)

  // Priority 1: Check for active alarms that are also offline (red)
  if (hasAlarm && isOffline) {
    return 'Alarm (Offline)'
  }

  // Priority 2: Check for active alarms (red)
  if (hasAlarm) {
    return 'Alarm'
  }

  // Priority 3: Check for warnings (amber)
  if (isWarningState(dp0AlarmState) || isWarningState(dp1AlarmState)) {
    return 'Warning'
  }

  // Priority 4: Check if offline (> 5 minutes no data)
  if (isOffline) {
    return 'Offline'
  }

  // Priority 5: Check if we have any data
  if (!lastSeenTimestamp) {
    return 'No Data'
  }

  // Default: All normal - green
  return 'Online'
}
